import { useTranslation } from 'react-i18next';
import { Info, Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { ADDRESS_KINDS, type AddressKind, type TechnicianAddress } from 'api/techniciansApi';
import { cn } from 'lib/utils';
import UnlocatedBadge from 'components/UnlocatedBadge';
import { unplacedAddress } from 'utils/unlocatedAddress';
import AddressAutocomplete from './AddressAutocomplete';
import {
  controlId,
  errorId,
  isFilledAddress,
  validationMessage,
  type AddressDraft,
  type FormErrors,
  type LogisticsForm,
} from './validation';

interface LogisticsSectionProps extends LogisticsForm {
  onChange: (patch: Partial<LogisticsForm>) => void;
  readOnly: boolean;
  /** Narrows the Places suggestions to the technician's country; empty = no restriction. */
  countryCode: string;
  /** As saved, for the geocoding hint only: the form never holds `geocodeState`. */
  stored: TechnicianAddress[];
  errors?: FormErrors;
}

// Billing carries no area (AC21), so only these two kinds map to a validated area field.
const AREA_FIELD = { delivery: 'deliveryAreaSqm', warehouse: 'warehouseAreaSqm' } as const;

const fieldId = (kind: AddressKind, part: string): string => `technician-address-${kind}-${part}`;

const errorText = 'text-xs text-rose-600 dark:text-rose-400';
const invalidInput = 'border-rose-400 focus-visible:ring-rose-400 dark:border-rose-500';
const hintText = 'text-xs text-zinc-500 dark:text-zinc-400';
// A row of the grouped address control: the frame draws the border, each row only its focus.
const segment = 'h-10 rounded-none border-0 shadow-none focus-visible:ring-inset';

type AddressText = Pick<TechnicianAddress, 'line1' | 'postalCode' | 'city'> | AddressDraft;

const formatAddress = ({ line1, postalCode, city }: AddressText): string =>
  [line1, [postalCode, city].filter(Boolean).join(' ')].filter(Boolean).join(', ');

/**
 * The three logistics addresses (L2c) — business premises, never a home (L2c-h). The address line
 * suggests Places results and stays typeable by hand (L2c-g). The reference is never chosen for
 * the person (R2).
 */
const LogisticsSection = ({
  addresses,
  referenceKind,
  onChange,
  readOnly,
  countryCode,
  stored,
  errors = {},
}: LogisticsSectionProps) => {
  const { t } = useTranslation();

  // Literal keys only: the i18n static guard cannot see a built one.
  const kindLabel: Record<AddressKind, string> = {
    billing: t('Billing address'),
    delivery: t('Delivery address'),
    warehouse: t('Warehouse address'),
  };
  const areaLabel = { delivery: t('Reception area'), warehouse: t('Warehouse area') };
  const kindHint = { delivery: t('For small parcels'), warehouse: t('For large parcels') };

  // Pending is a normal state, never a warning (L2c-i); only a failed lookup is amber.
  const geocodeNotice = (saved: TechnicianAddress | null) =>
    saved?.geocodeState === 'pending' ? (
      <span className={hintText}>{t('Position being computed')}</span>
    ) : saved?.geocodeState === 'not_found' ? (
      <div className="flex flex-col gap-1">
        <UnlocatedBadge label={t('Not located')} />
        <span className="text-xs text-amber-800 dark:text-amber-300">
          {t('The distance cannot be computed for this address. Check it.')}
        </span>
      </div>
    ) : null;

  // Edit mode: only while the draft still says what was saved, the geocoding verdict being about that text.
  const editedNotice = (kind: AddressKind) => {
    const saved = unplacedAddress(stored, kind);
    return saved && formatAddress(saved) === formatAddress(addresses[kind]) ? geocodeNotice(saved) : null;
  };

  // Any typed change drops the Places id: it named the address as it was before the edit.
  const setDraft = (kind: AddressKind, patch: Partial<AddressDraft>, placeId: string | null = null) =>
    onChange({ addresses: { ...addresses, [kind]: { ...addresses[kind], ...patch, placeId } } });

  const streetInput = (kind: AddressKind) => (
    <AddressAutocomplete
      id={fieldId(kind, 'line1')}
      value={addresses[kind].line1}
      country={countryCode || null}
      placeholder={t('Street number and name')}
      className={cn(segment, 'rounded-t-md')}
      onType={(line1) => setDraft(kind, { line1 })}
      onPick={(patch, placeId) => setDraft(kind, patch, placeId)}
    />
  );

  // One framed control per address: street on top, postal code | city below (maquette 1a).
  const addressField = (kind: AddressKind) => (
    <div role="group" aria-labelledby={fieldId(kind, 'label')} className="min-w-0 space-y-1.5">
      <Label id={fieldId(kind, 'label')} htmlFor={fieldId(kind, 'line1')}>
        {kindLabel[kind]}
      </Label>
      <div className="rounded-md border border-input shadow-sm">
        {streetInput(kind)}
        <div className="flex border-t border-input">
          <Input
            id={fieldId(kind, 'postalCode')}
            value={addresses[kind].postalCode}
            aria-label={t('Postal code')}
            placeholder={t('Postal code')}
            className={cn(segment, 'w-[7.5rem] shrink-0 rounded-bl-md border-r border-input font-mono')}
            onChange={(event) => setDraft(kind, { postalCode: event.target.value })}
          />
          <Input
            id={fieldId(kind, 'city')}
            value={addresses[kind].city}
            aria-label={t('City')}
            placeholder={t('City')}
            className={cn(segment, 'min-w-0 rounded-br-md')}
            onChange={(event) => setDraft(kind, { city: event.target.value })}
          />
        </div>
      </div>
      {kind !== 'billing' && <p className={hintText}>{kindHint[kind]}</p>}
      {editedNotice(kind)}
    </div>
  );

  const areaField = (kind: 'delivery' | 'warehouse') => {
    const field = AREA_FIELD[kind];
    const code = errors[field];
    return (
      <div className="min-w-0 space-y-1.5">
        <Label htmlFor={controlId(field)}>
          {areaLabel[kind]}
          <span className="sr-only"> (m²)</span>
        </Label>
        <div className="relative">
          <Input
            id={controlId(field)}
            inputMode="decimal"
            value={addresses[kind].areaSqm}
            aria-invalid={code ? true : undefined}
            aria-describedby={code ? errorId(field) : undefined}
            className={cn('h-10 pr-10 font-mono', code && invalidInput)}
            onChange={(event) =>
              onChange({ addresses: { ...addresses, [kind]: { ...addresses[kind], areaSqm: event.target.value } } })
            }
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-zinc-400 dark:text-zinc-500"
          >
            m²
          </span>
        </div>
        {code && (
          <p id={errorId(field)} className={errorText}>
            {validationMessage(t, code)}
          </p>
        )}
      </div>
    );
  };

  const header = (
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <Truck className="h-4 w-4" aria-hidden="true" />
        {t('Delivery / logistics')}
      </CardTitle>
      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        {t('Addresses and spaces used when equipment has to be sent to the technician.')}
      </p>
    </CardHeader>
  );

  const footnote = (
    <p className="flex items-start gap-2 text-xs leading-[1.5] text-zinc-500 dark:text-zinc-400">
      <Info className="mt-px h-[15px] w-[15px] shrink-0" aria-hidden="true" />
      {t('The delivery address is used for small parcels; the warehouse for large ones.')}
    </p>
  );

  if (readOnly) {
    const filled = ADDRESS_KINDS.filter((kind) => isFilledAddress(addresses[kind]));
    return (
      <Card>
        {header}
        <CardContent className="space-y-3">
          {filled.length === 0 ? (
            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('No logistics address is filled in yet.')}</p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {filled.map((kind) => {
                const draft = addresses[kind];
                const isReference = kind === referenceKind;
                return (
                  <li
                    key={kind}
                    className={cn(
                      'flex min-w-0 flex-col gap-[5px] rounded-[10px] border px-3.5 py-3',
                      isReference
                        ? 'border-sky-500 bg-sky-50 dark:border-sky-400 dark:bg-sky-950/40'
                        : 'border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/40'
                    )}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[11.5px] font-medium uppercase tracking-[0.05em] text-zinc-500 dark:text-zinc-400">
                        {kindLabel[kind]}
                      </span>
                      {isReference && (
                        <span className="inline-flex h-[19px] items-center rounded-full bg-sky-700 px-[7px] text-[10.5px] font-semibold uppercase tracking-[0.02em] text-white dark:bg-sky-700">
                          {t('Default')}
                        </span>
                      )}
                    </span>
                    <span className="text-[13.5px] font-medium leading-[1.45] text-zinc-900 dark:text-zinc-50">
                      {formatAddress(draft)}
                    </span>
                    {kind !== 'billing' && draft.areaSqm && (
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">
                        {areaLabel[kind]} · <span className="font-mono">{draft.areaSqm} m²</span>
                      </span>
                    )}
                    {geocodeNotice(unplacedAddress(stored, kind))}
                  </li>
                );
              })}
            </ul>
          )}
          {footnote}
        </CardContent>
      </Card>
    );
  }

  const referenceError = errors.referenceKind;

  return (
    <Card>
      {header}
      <CardContent className="space-y-4">
        {addressField('billing')}
        <div className="grid gap-4 md:grid-cols-2">
          {areaField('delivery')}
          {addressField('delivery')}
          {areaField('warehouse')}
          {addressField('warehouse')}
        </div>

        <div className="space-y-2.5 rounded-[10px] border border-zinc-200 bg-zinc-50 px-3.5 py-3 dark:border-zinc-800 dark:bg-zinc-900/40">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <span id="technician-reference-label" className="text-sm font-medium text-zinc-900 dark:text-zinc-50">
              {t('Default address')}
            </span>
            <span className="text-[11.5px] text-zinc-500 dark:text-zinc-400">
              {t('Reference address used to propose the technician according to their location')}
            </span>
          </div>
          {/* Native radios under the chips: arrow keys and "one checked at most" come for free (AC1).
              tabIndex -1 so a refused save can move focus onto the group. */}
          <div
            id={controlId('referenceKind')}
            role="radiogroup"
            tabIndex={-1}
            aria-labelledby="technician-reference-label"
            aria-invalid={referenceError ? true : undefined}
            aria-describedby={referenceError ? errorId('referenceKind') : undefined}
            className="flex flex-wrap gap-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
          >
            {ADDRESS_KINDS.map((kind) => {
              const checked = kind === referenceKind;
              return (
                <label
                  key={kind}
                  className={cn(
                    'inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border pl-[11px] pr-[13px] text-[12.5px] font-medium has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-zinc-900 dark:has-[:focus-visible]:ring-zinc-100',
                    checked
                      ? 'border-sky-500 bg-sky-50 text-sky-700 dark:border-sky-400 dark:bg-sky-950/40 dark:text-sky-300'
                      : 'border-zinc-300 text-zinc-500 dark:border-zinc-700 dark:text-zinc-400'
                  )}
                >
                  <input
                    type="radio"
                    name="technician-reference-address"
                    value={kind}
                    checked={checked}
                    onChange={() => onChange({ referenceKind: kind })}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
                      checked ? 'border-sky-500 dark:border-sky-400' : 'border-zinc-400 dark:border-zinc-600'
                    )}
                  >
                    {checked && <span className="h-[7px] w-[7px] rounded-full bg-sky-500 dark:bg-sky-400" />}
                  </span>
                  {kindLabel[kind]}
                </label>
              );
            })}
          </div>
          {referenceKind && isFilledAddress(addresses[referenceKind]) && (
            <p className={hintText}>
              {kindLabel[referenceKind]} · {formatAddress(addresses[referenceKind])}
            </p>
          )}
          {referenceError && (
            <p id={errorId('referenceKind')} className={errorText}>
              {validationMessage(t, referenceError)}
            </p>
          )}
        </div>

        {footnote}
      </CardContent>
    </Card>
  );
};

export default LogisticsSection;
